/**
 * Single-order date and range predicates for sibling-order filtering.
 * @module siblingOrderFilter/orderPredicates
 */
import { SiblingOrder } from "../../../data-model/siblingOrdersModel";
import { toTime, toYearMonthKey } from "./dateUtils";

/**
 * Determines whether an order's date of service falls within an inclusive
 * date range (both start and end dates are treated as in-range).
 *
 * @param order - The order whose date of service is being checked
 * @param startDate - The inclusive start of the range, parseable by `Date`
 * @param endDate - The inclusive end of the range, parseable by `Date`
 * @returns `true` when the order's date of service lies on or between the start
 *   and end dates; `false` when any of the dates are missing or invalid
 */
export const isOrderDosWithinRange = (
  order: SiblingOrder,
  startDate: string,
  endDate: string,
): boolean => {
  const orderTime = toTime(order?.order?.payload?.date_of_service);
  const startTime = toTime(startDate);
  const endTime = toTime(endDate);
  if (orderTime === null || startTime === null || endTime === null) {
    return false;
  }
  return orderTime >= startTime && orderTime <= endTime;
};

/**
 * Determines whether a single order's date of service shares the parent order's
 * service month and year.
 *
 * @param parentOrderDos - The parent order's date of service reference point
 * @param order - The order whose date of service is being checked
 * @returns `true` when the order falls in the parent's service month/year;
 *   `false` when the parent's date of service is missing or invalid
 */
export const isOrderInParentMonthYear = (
  parentOrderDos: string,
  order: SiblingOrder,
): boolean => {
  const parentKey = toYearMonthKey(parentOrderDos);
  if (!parentKey) {
    return false;
  }
  return toYearMonthKey(order?.order?.payload?.date_of_service) === parentKey;
};

/**
 * Determines whether a single order is upcoming relative to the parent order,
 * i.e. its date of service is strictly after the parent order's date of service.
 *
 * @param parentOrderDos - The parent order's date of service reference point
 * @param order - The order whose date of service is being checked
 * @returns `true` when the order's date of service is later than the parent's;
 *   `false` when either date is missing or invalid
 */
export const isOrderUpcoming = (
  parentOrderDos: string,
  order: SiblingOrder,
): boolean => {
  const parentTime = toTime(parentOrderDos);
  const orderTime = toTime(order?.order?.payload?.date_of_service);
  if (parentTime === null || orderTime === null) {
    return false;
  }
  return orderTime > parentTime;
};
